import React, { useEffect, useMemo, useRef, useState } from 'react';

const AnimatedResponse = ({
  text = '',
  speed = 40,
  isDark = false,
  onComplete,
  renderText,
}) => {
  const fullText = String(text || '');
  const [displayCount, setDisplayCount] = useState(0);
  const completedRef = useRef(false);

  const resolvedSpeed = useMemo(() => {
    const numeric = Number(speed);
    if (!Number.isFinite(numeric) || numeric <= 0) return 40;
    return Math.min(120, Math.max(10, numeric));
  }, [speed]);

  useEffect(() => {
    setDisplayCount(0);
    completedRef.current = false;

    if (!fullText) {
      completedRef.current = true;
      onComplete?.();
      return undefined;
    }

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setDisplayCount(fullText.length);
      completedRef.current = true;
      onComplete?.();
      return undefined;
    }

    const tickMs = 33;
    const charsPerTick = Math.max(1, Math.round((resolvedSpeed * tickMs) / 1000));

    const timer = setInterval(() => {
      setDisplayCount((prev) => {
        const next = Math.min(fullText.length, prev + charsPerTick);
        if (next >= fullText.length && !completedRef.current) {
          completedRef.current = true;
          onComplete?.();
        }
        return next;
      });
    }, tickMs);

    return () => clearInterval(timer);
  }, [fullText, onComplete, resolvedSpeed]);

  const visibleText = fullText.slice(0, displayCount);
  const isComplete = displayCount >= fullText.length;

  if (typeof renderText === 'function') {
    return renderText(visibleText, isComplete);
  }

  return (
    <span
      className="whitespace-pre-wrap"
      style={{ color: isDark ? '#e5e5e5' : '#1a1a1a' }}
    >
      {visibleText}
      {!isComplete && (
        <span className="inline-block w-[0.5ch] animate-pulse text-[#E87A2A] align-baseline">|</span>
      )}
    </span>
  );
};

export default AnimatedResponse;
